import { yupResolver } from '@hookform/resolvers/yup';
import { Divider, Typography } from '@mui/material';
import { Resolver, useFieldArray, useForm } from 'react-hook-form';
import { Trans } from 'react-i18next';

import { getAssetPath } from '@/constants/configs';
import { MAX_ASSETS } from '@/constants/finances';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectDefaultCurrency } from '@/redux/configs/baseSelectors';

import { FinancialType } from '@/types/finance';

import { FinanceQuestion, useAddFinancialInfo } from '../../hooks';
import { AddBlockButton, FinancesQuestionForm } from '../common';
import { AssetBlock } from './AssetBlock';
import { AssetsTotalLine } from './AssetsTotalLine';
import { AssetFormValues } from './type';
import { getAssetsValidationSchema } from './validationSchema';

type QuestionAssetsProps = {
  onSubmit: () => void;
};

export function QuestionAssets({ onSubmit }: Readonly<QuestionAssetsProps>) {
  const { t } = useTranslate();
  const { mutate, isPending } = useAddFinancialInfo({
    onSuccess: onSubmit,
  });
  const { currency, currencyDisplay } = useAppSelector(selectDefaultCurrency);

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<AssetFormValues>({
    defaultValues: {
      assets: [{ assetType: '', amount: '' }],
    },
    mode: 'onChange',
    resolver: yupResolver(
      getAssetsValidationSchema(t),
    ) as Resolver<AssetFormValues>,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'assets',
  });

  const handleAddAsset = () => {
    if (fields.length < MAX_ASSETS) {
      append({ assetType: '', amount: '' });
    }
  };

  const handleDeleteAsset = (index: number) => {
    remove(index);
  };

  const onContinue = handleSubmit(data => {
    const payload = data.assets.map(asset => ({
      financialType: FinancialType.Asset,
      financialSubtype: asset.assetType,
      amount: Number(asset.amount),
      currency,
    }));
    mutate(payload);
  });

  return (
    <FinancesQuestionForm
      question={FinanceQuestion.Assets}
      title={t('moreInfo.form.yourFinances.assets.title')}
      imageUrl={getAssetPath('/assets/images/icon-assets.svg')}
      onContinue={onContinue}
      disable={!isValid}
      isLoading={isPending}
      subtitle={
        <Trans
          i18nKey="moreInfo.form.yourFinances.assets.subtitle"
          components={{
            bold: (
              <Typography
                component="span"
                variant="body2Bold"
                color="neutral.1000"
              />
            ),
          }}
        />
      }
    >
      {fields.map((field, index) => (
        <AssetBlock
          key={field.id}
          index={index}
          total={fields.length}
          currencyDisplay={currencyDisplay}
          control={control}
          onDelete={() => handleDeleteAsset(index)}
        />
      ))}

      {fields.length < MAX_ASSETS && (
        <AddBlockButton
          label={t('moreInfo.form.yourFinances.assets.addAsset')}
          onClick={handleAddAsset}
        />
      )}

      <Divider sx={{ mb: 2 }} />

      <AssetsTotalLine control={control} currencyDisplay={currencyDisplay} />
    </FinancesQuestionForm>
  );
}
